/**
 * P9-P2B — Public language switcher.
 *
 * Renders ONLY the languages the platform has activated (`active_languages`).
 * The switcher renders only languages enabled in public `active_languages`; the
 * Admin Localization setting remains the rollout gate for all 11 launch languages. Hidden on `/admin/*`
 * (admin is English-only). Selection persists for authenticated users via the
 * backend preference + locally for guests.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { useAuth } from '../../context/AuthContext';
import { applyLanguage } from '../../i18n/languageManager';
import { Icon } from './Icon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const LABELS = {
  en: 'English',
  ar: 'العربية',
  ur: 'اردو',
  hi: 'हिन्दी',
  ml: 'മലയാളം',
  zh: '中文',
  fr: 'Français',
  de: 'Deutsch',
  es: 'Español',
  pt: 'Português',
  tr: 'Türkçe',
};

export function LanguageSwitcher({ className = '' }) {
  const { t, i18n } = useTranslation('shared');
  const { active_languages } = useSiteSettings();
  const { isAuthenticated, updateUserLanguage } = useAuth();
  const location = useLocation();

  const langs = Array.isArray(active_languages) ? active_languages : ['en'];

  // Gated: nothing to switch (single active language) or admin area → render nothing.
  if (langs.length <= 1 || location.pathname.startsWith('/admin')) return null;

  const current = (i18n.language || 'en').split('-')[0];

  const handleSelect = (code) => {
    if (code === current) return;
    if (isAuthenticated) updateUserLanguage(code);
    else applyLanguage(code);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`inline-flex items-center gap-1.5 text-sm font-medium brand-header-link ${className}`}
          data-testid="language-switcher-trigger"
          aria-label={t('languageSwitcher.changeLanguage')}
        >
          <Icon name="languages" className="h-4 w-4" />
          <span>{LABELS[current] || current.toUpperCase()}</span>
          <Icon name="chevron-down" className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[8rem]">
        {langs.map((code) => (
          <DropdownMenuItem
            key={code}
            onClick={() => handleSelect(code)}
            className="cursor-pointer"
            data-testid={`language-option-${code}`}
          >
            <span className={code === current ? 'font-semibold' : ''}>
              {LABELS[code] || code.toUpperCase()}
            </span>
            {code === current && <Icon name="check" className="ms-auto h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSwitcher;
